import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useBlocker } from "@tanstack/react-router";
import {
  BookOpen,
  Check,
  Clock3,
  Ellipsis,
  History,
  Maximize2,
  Minimize2,
  Paperclip,
  Pencil,
  Type,
} from "lucide-react";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import {
  DOC_PERMISSION_RANK,
  type DocPageWithContent,
  type WriteRejection,
  type WriteResult,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";
import { SELECTED_CHOICE } from "@/components/list/selectedChoice";
import { cn } from "@/lib/utils";
import {
  bodyStart,
  imageResolver,
  opensWithTitle,
  renderMarkdown,
  withoutOpeningTitle,
} from "@/modules/docs/markdown";
import { AttachmentsSection } from "@/modules/docs/screens/Attachments";
import { RejectionNotice } from "@/modules/docs/screens/RejectionNotice";
import { VersionsSheet } from "@/modules/docs/screens/VersionsSheet";
import { useAttachments } from "@/modules/docs/useAttachments";
import { useDocsPeople } from "@/modules/docs/useDocsPeople";
import {
  formatAbsolute,
  formatBanner,
  relativeTime,
} from "@/modules/docs/versions";

/**
 * The CodeMirror editor, loaded only once a page is actually put into edit
 * mode (#390). Nothing above this line imports `@codemirror/*`, which is
 * what keeps those packages out of the chunk a Read-only visitor — or
 * anyone just browsing — downloads to see this screen at all.
 */
const MarkdownEditor = lazy(
  () => import("@/modules/docs/editor/MarkdownEditor"),
);

/**
 * One page: its rendered content, an Obsidian-style live-preview editor for
 * it, and its versions.
 *
 * `Browse` mounts this with `key={page.id}`, so switching pages remounts it
 * rather than needing an effect to reset `draft` back to the new page's
 * content — a `useEffect` doing that would be a `set-state-in-effect`,
 * which CLAUDE.md's style rules treat as a defect: the state should be
 * derived, and a fresh mount is how it is derived here.
 */
export function PageReader({
  page,
  folderPath,
  initialMode,
  onEditSessionEnd,
}: {
  page: DocPageWithContent;
  /** The folder and its parents by name: "Finance / GST". */
  folderPath: string;
  /**
   * How the page opens. Absent, someone who can write gets the editor, ready
   * to type, and everyone else the reading view (#641).
   */
  initialMode?: "read" | "edit";
  /**
   * Called once, the first time this mount leaves edit mode (switching back
   * to reading). `Browse` uses it to retire the "start this one in edit
   * mode" flag it set when the page was created — from the event that ends
   * the session, not an effect watching for it, so the flag changes without
   * a second render of its own.
   */
  onEditSessionEnd?: () => void;
}) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const people = useDocsPeople();

  const isMobile = useIsMobile();
  const attachments = useAttachments(page.id);
  // A pasted image is `![name](/docs/attachments/<id>)`; the picture itself
  // loads from the authenticated download route, on the API's origin.
  const resolveImage = useMemo(
    () => imageResolver((id) => data.docs.attachmentUrl(id)),
    [data],
  );

  // A courtesy, not the enforcement — `savePage` refuses the write itself
  // regardless of whether this button was ever shown (FR-DOC-8's whole
  // point). Hiding it just spares a Read-only visitor a click that would
  // only ever come back `forbidden`.
  const canWrite =
    DOC_PERMISSION_RANK[page.effectiveLevel] >= DOC_PERMISSION_RANK.write;

  const [mode, setMode] = useState<"read" | "edit">(
    initialMode ?? (canWrite ? "edit" : "read"),
  );
  // Focus mode (#641): the page alone, over everything, in a ~720px column.
  const [focus, setFocus] = useState(false);
  // The editor without its live preview: the Markdown as typed.
  const [raw, setRaw] = useState(false);
  const [draft, setDraft] = useState(page.revision.markdown);
  const [titleDraft, setTitleDraft] = useState(page.title);
  const [renamingTitle, setRenamingTitle] = useState(false);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [viewingRevisionId, setViewingRevisionId] = useState<string | null>(
    null,
  );

  const bodyDirty = mode === "edit" && draft !== page.revision.markdown;

  // FR-DOC-4's whole point cuts both ways: leaving mid-edit should cost a
  // confirmation the same way leaving mid-form does anywhere else in this
  // application, whether "leaving" is the tab closing or clicking a
  // different page in the tree two panes over — both go through the
  // router's own history, so one blocker covers both. A pending title
  // rename does not need its own check here: Enter commits it immediately
  // and Escape (or losing focus) discards it, so there is never a moment a
  // navigation could silently lose it.
  useBlocker(
    () =>
      !window.confirm(
        "You have unsaved edits to this page. Leave without saving?",
      ),
    bodyDirty,
  );

  const revisionsQuery = useQuery({
    queryKey: ["docs", "revisions", page.id],
    queryFn: () => data.docs.listRevisions(page.id, { limit: 100 }),
  });
  const viewingRevision = viewingRevisionId
    ? (revisionsQuery.data?.items.find((r) => r.id === viewingRevisionId) ??
      null)
    : null;

  // Saving writes a new revision (FR-DOC-3); it never carries a message —
  // the owner dropped the "what changed and why" box on #380 (§7.8) because
  // one page that renders while you type does not want a second form
  // beside it. Renaming is its own action (`renameMutation`, below), not
  // folded into this one, because it commits the moment Enter is pressed
  // rather than waiting for the body to be saved too.
  const saveMutation = useMutation({
    mutationFn: (): Promise<WriteResult<DocPageWithContent>> =>
      data.docs.savePage(page.id, draft),
    onSuccess: (result) => {
      if (result.ok) {
        setRejection(null);
        // Write-through: the reader below redraws from whatever this
        // refetches, never from `draft` — a rejected save must leave the
        // page exactly as it was, and a save that succeeds should show the
        // channel's own new revision rather than the form that produced it.
        void queryClient.invalidateQueries({ queryKey: ["docs"] });
      } else {
        setRejection(result.rejection);
      }
    },
  });

  /**
   * Saves the body if it actually changed, then runs `onSaved` — used by
   * both Ctrl/Cmd+S (no `onSaved`: stay in edit mode either way) and the
   * book icon (switches to reading, but only once the save has actually
   * gone through — a rejection has to leave the draft on screen, not throw
   * it away by switching modes anyway).
   *
   * Skipping the call entirely when nothing changed is what "no-op saves
   * create nothing" (#390) means in practice: there is no request to
   * short-circuit server-side, because none is made.
   */
  function saveBodyIfChanged(onSaved?: () => void) {
    if (draft === page.revision.markdown) {
      onSaved?.();
      return;
    }
    saveMutation.mutate(undefined, {
      onSuccess: (result) => {
        if (result.ok) onSaved?.();
      },
    });
  }

  const renameMutation = useMutation({
    mutationFn: (title: string) => data.docs.renamePage(page.id, title),
    onSuccess: (result) => {
      if (result.ok) {
        setRenamingTitle(false);
        setRejection(null);
        void queryClient.invalidateQueries({ queryKey: ["docs"] });
      } else {
        setRejection(result.rejection);
      }
    },
  });

  function cancelRename() {
    setTitleDraft(page.title);
    setRenamingTitle(false);
  }

  function commitRename() {
    const trimmed = titleDraft.trim();
    if (trimmed === "" || trimmed === page.title) {
      cancelRename();
      return;
    }
    renameMutation.mutate(trimmed);
  }

  function enterEditMode() {
    setDraft(page.revision.markdown);
    setTitleDraft(page.title);
    setRenamingTitle(false);
    setRejection(null);
    setMode("edit");
  }

  function toggleMode() {
    if (mode === "read") {
      enterEditMode();
      return;
    }
    saveBodyIfChanged(() => {
      setMode("read");
      onEditSessionEnd?.();
    });
  }

  const restoreMutation = useMutation({
    mutationFn: (revisionId: string) =>
      data.docs.restoreRevision(page.id, revisionId),
    onSuccess: (result) => {
      if (result.ok) {
        setViewingRevisionId(null);
        setRejection(null);
        void queryClient.invalidateQueries({ queryKey: ["docs"] });
      } else {
        setRejection(result.rejection);
      }
    },
  });

  // Ctrl+Shift+F opens and closes focus mode; Esc leaves it (#641). On the
  // window, so it works with the cursor in the editor or anywhere else.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey &&
        event.key.toLowerCase() === "f"
      ) {
        event.preventDefault();
        setFocus((was) => !was);
      } else if (event.key === "Escape" && focus) {
        setFocus(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [focus]);

  const displayedMarkdown = viewingRevision
    ? viewingRevision.markdown
    : page.revision.markdown;

  // Hidden while viewing an old revision — editing one is "restore it
  // first", not a live edit of history, the same reason the old Edit button
  // never showed there either.
  const showModeToggle = mode === "edit" || (canWrite && !viewingRevision);

  const editing = mode === "edit";
  // The title once (#641): the body's own opening "# Title" stands in for the
  // heading while editing, and is left out of the reading view.
  const bodyHasTitle = opensWithTitle(
    editing ? draft : displayedMarkdown,
    page.title,
  );
  const showHeading = renamingTitle || !editing || !bodyHasTitle;
  // Always there while editing (#628): it says "Saved" when there is nothing
  // to save, rather than appearing only once there is, so it is findable
  // before the first keystroke — and on a phone, which has no Ctrl+S.
  const saveButton = editing ? (
    <Button
      type="button"
      size="sm"
      variant={bodyDirty ? "default" : "ghost"}
      disabled={!bodyDirty || saveMutation.isPending}
      title="Save (Ctrl+S)"
      className={cn(!bodyDirty && "text-muted-foreground")}
      onClick={() => {
        saveBodyIfChanged();
      }}
    >
      {saveMutation.isPending ? (
        "Saving…"
      ) : bodyDirty ? (
        "Save"
      ) : (
        <>
          <Check /> Saved
        </>
      )}
    </Button>
  ) : null;

  const heading = renamingTitle ? (
    <Input
      autoFocus
      value={titleDraft}
      onChange={(event) => {
        setTitleDraft(event.target.value);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          commitRename();
        } else if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          cancelRename();
        }
      }}
      aria-label="Page title"
      className="h-10 max-w-lg text-xl font-semibold"
    />
  ) : showHeading ? (
    <h1 className="text-3xl leading-tight font-semibold tracking-tight text-balance">
      {page.title}
    </h1>
  ) : null;

  const body = (
    <div className="space-y-4">
      {viewingRevision ? (
        <div className="bg-muted/40 flex flex-wrap items-center gap-x-1.5 gap-y-1 rounded-md border px-3 py-2 text-sm">
          <Clock3
            className="text-muted-foreground size-4 shrink-0"
            aria-hidden
          />
          <span>
            Viewing version from {formatBanner(viewingRevision.createdAt)} by{" "}
            {people.principalName("user", viewingRevision.authorUserId)}
          </span>
          <span className="text-muted-foreground">—</span>
          <button
            type="button"
            className="text-primary underline-offset-2 hover:underline"
            onClick={() => {
              setViewingRevisionId(null);
            }}
          >
            Back to current
          </button>
          <span className="text-muted-foreground">·</span>
          <button
            type="button"
            className="text-primary underline-offset-2 hover:underline disabled:pointer-events-none disabled:opacity-50"
            disabled={restoreMutation.isPending}
            onClick={() => {
              restoreMutation.mutate(viewingRevision.id);
            }}
          >
            Restore this version
          </button>
        </div>
      ) : null}

      {viewingRevision && rejection ? (
        <RejectionNotice rejection={rejection} />
      ) : null}

      {editing ? (
        <div className="space-y-2">
          <Suspense
            fallback={<Skeleton className="min-h-[50vh] w-full rounded-md" />}
          >
            <MarkdownEditor
              value={draft}
              onChange={setDraft}
              onSave={() => {
                saveBodyIfChanged();
              }}
              ariaLabel={`Edit ${page.title}`}
              raw={raw}
              autoFocus={!isMobile}
              initialCursor={bodyStart(draft, page.title)}
              resolveImage={resolveImage}
              onPasteImage={canWrite ? attachments.uploadInline : undefined}
            />
          </Suspense>
          {!viewingRevision && rejection ? (
            <RejectionNotice rejection={rejection} />
          ) : null}
        </div>
      ) : (
        renderMarkdown(
          withoutOpeningTitle(displayedMarkdown, page.title),
          resolveImage,
        )
      )}

      {/* Only once there is something to list (#641); "Attach a file" is in
          the "⋯" menu, and a file can be dropped anywhere on the page. */}
      {!viewingRevision ? (
        <AttachmentsSection
          state={attachments}
          canWrite={canWrite}
          copyLinks={editing}
        />
      ) : null}
    </div>
  );

  if (focus) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Focus mode: ${page.title}`}
        className="bg-background fixed inset-0 z-50 overflow-auto"
        {...attachments.dropProps(canWrite)}
      >
        {attachments.picker}
        <div className="bg-background/90 text-muted-foreground sticky top-0 z-10 flex items-center gap-3 px-4 py-2.5 text-xs opacity-70 backdrop-blur-sm transition-opacity hover:opacity-100 sm:px-6">
          <span className="min-w-0 flex-1 truncate">{page.title}</span>
          {saveButton}
          <kbd className="bg-muted hidden rounded border px-1.5 py-0.5 font-mono text-[11px] sm:inline">
            Esc
          </kbd>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label="Leave focus mode"
            onClick={() => {
              setFocus(false);
            }}
          >
            <Minimize2 />
          </Button>
        </div>
        <div className="mx-auto w-full max-w-[720px] space-y-6 px-5 pt-8 pb-32 sm:px-8 sm:pt-14">
          {heading}
          {body}
        </div>
      </div>
    );
  }

  return (
    /*
      Calm by default (#641): a muted line with where the page lives and who
      last edited it, a few quiet icons, the title once, then the page. The
      icons sit at the pane's far right (#403); the text keeps a reading
      measure.
    */
    <div
      className={cn(
        "space-y-5 rounded-md transition-shadow",
        attachments.dragOver && "ring-primary/60 ring-2 ring-offset-4",
      )}
      {...attachments.dropProps(canWrite)}
    >
      {attachments.picker}
      <header className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-muted-foreground min-w-0 flex-1 truncate text-xs">
            {folderPath ? `${folderPath} · ` : ""}
            <span title={formatAbsolute(page.revision.createdAt)}>
              edited {relativeTime(page.revision.createdAt)} by{" "}
              {people.principalName("user", page.revision.authorUserId)}
            </span>
          </p>

          <div className="flex shrink-0 items-center gap-1">
            {saveButton}
            {editing ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-pressed={raw}
                className={cn("text-muted-foreground", raw && SELECTED_CHOICE)}
                onClick={() => {
                  setRaw((was) => !was);
                }}
              >
                <Type /> Raw
              </Button>
            ) : null}
            {showModeToggle ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="icon-sm"
                    variant="ghost"
                    aria-label={editing ? "Reading view" : "Edit"}
                    onClick={toggleMode}
                  >
                    {editing ? <BookOpen /> : <Pencil />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {editing ? "Reading view" : "Edit"}
                </TooltipContent>
              </Tooltip>
            ) : null}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Focus mode"
                  onClick={() => {
                    setFocus(true);
                  }}
                >
                  <Maximize2 />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Focus mode (Ctrl+Shift+F)</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Versions"
                  onClick={() => {
                    setVersionsOpen(true);
                  }}
                >
                  <History />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Versions</TooltipContent>
            </Tooltip>
            {canWrite && !viewingRevision ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="icon-sm"
                    variant="ghost"
                    aria-label="More page actions"
                  >
                    <Ellipsis />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onSelect={() => {
                      setTitleDraft(page.title);
                      setRenamingTitle(true);
                    }}
                  >
                    <Pencil /> Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={attachments.openPicker}>
                    <Paperclip /> Attach a file
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        </div>
        {heading}
      </header>

      <div className="max-w-3xl">{body}</div>

      <VersionsSheet
        pageId={page.id}
        currentRevisionId={page.currentRevisionId}
        viewingRevisionId={viewingRevisionId}
        open={versionsOpen}
        onOpenChange={setVersionsOpen}
        onView={(revisionId) => {
          setViewingRevisionId(revisionId);
          setVersionsOpen(false);
          // Viewing an old revision is read-only (§7.8) — an edit in
          // progress is abandoned the same way `restoreRevision` documents
          // restoring one is: nothing here rewrites what was being edited,
          // it is just no longer what is on screen.
          setMode("read");
        }}
      />
    </div>
  );
}
